import { ReactNode, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { ListItemText, MenuItem } from "@mui/material"
import { format, subDays, parseISO } from "date-fns"

import Selector from "@/components/form/selector/Selector"
import Checkbox from "@/components/form/checkbox/Checkbox"
import RadioGroup from "@/components/form/radioGroup/RadioGroup"
import { CheckboxColor } from "@/types/components/checkbox"
import DatePicker from "@/components/form/datePicker/DatePicker"
import { REPORT_TYPE } from "@/app/report/common/constants"
import { ActivityTypeKey } from "@/constants/activity"
import { MatchDayType } from "@/types/activity"
import { useReportTemplateMetasQuery } from "@/hooks/api/useReportQuery"
import { usePdfTemplatesQuery } from "@/hooks/api/usePdfTemplateQuery"
import { useSeasonWeekQuery, usePlayersQuery } from "@/hooks/api/useSettingQuery"
import type { FilterPresetPeriodType, UpsertFilterPresetPayload } from "@/types/filterPreset"
import { PERIOD_LABELS } from "@/utils/filterPreset"
import PlayerListSelector from "./PlayerListSelector"
import { PERIOD_OPTIONS, ACTIVITY_TYPE_OPTIONS, MATCH_DAY_OPTIONS } from "./constants"

import "./filterPreset.scss"

const NONE_SEASON_WEEK_ID = -1

interface ReportFilterFieldsProps {
  form: UpsertFilterPresetPayload
  updateForm: (updates: Partial<UpsertFilterPresetPayload>) => void
  /** PDF 템플릿 셀렉터 노출 여부 (프리셋: true, CSV Export: false) */
  showPdfTemplate?: boolean
  /** Section 1 상단에 끼워넣을 노드 (프리셋 이름 필드 등) */
  nameSlot?: ReactNode
}

/**
 * 리포트 필터 입력 본문 (리포트 타입 / 템플릿 / 기간 / 액티비티타입·시즌·매치데이 / 선수 선택).
 * Filter Preset 에디터에서 사용. (CSV Export 는 자체 마크업 — CsvDetailedFilters)
 * 상태는 UpsertFilterPresetPayload 를 그대로 사용.
 */
export default function ReportFilterFields({
  form,
  updateForm,
  showPdfTemplate = true,
  nameSlot,
}: ReportFilterFieldsProps) {
  const { t } = useTranslation()

  // --- Queries ---
  const { data: templates } = useReportTemplateMetasQuery({ reportType: form.reportType }, true)
  const { data: pdfTemplates } = usePdfTemplatesQuery({ reportType: form.reportType })
  const { data: seasonWeekData } = useSeasonWeekQuery()
  const { data: players } = usePlayersQuery()

  const activeSeasonWeeks = useMemo(
    () => seasonWeekData?.seasonWeeks?.filter((sw) => sw.active) ?? [],
    [seasonWeekData],
  )

  const allSeasonWeekIds = useMemo(
    () => [...activeSeasonWeeks.map((sw) => sw.idfSeasonWeek as number), NONE_SEASON_WEEK_ID],
    [activeSeasonWeeks],
  )

  // --- 빈 배열이면 전체 선택으로 초기화 ---
  useEffect(() => {
    if (form.activityTypes.length === 0) {
      updateForm({ activityTypes: ACTIVITY_TYPE_OPTIONS.map((o) => o.value) })
    }
  }, [])

  useEffect(() => {
    if (allSeasonWeekIds.length > 0 && form.seasonWeekIds.length === 0) {
      updateForm({ seasonWeekIds: [...allSeasonWeekIds] })
    }
  }, [allSeasonWeekIds])

  useEffect(() => {
    if (form.matchDayTypes.length === 0) {
      updateForm({ matchDayTypes: [...MATCH_DAY_OPTIONS] })
    }
  }, [])

  useEffect(() => {
    if (players?.length && form.selectedPlayerIds.length === 0) {
      updateForm({ selectedPlayerIds: players.map((p) => p.idfPlayer) })
    }
  }, [players])

  // --- Handlers ---
  const handleReportTypeChange = (reportType: REPORT_TYPE) => {
    updateForm({ reportType, idfTemplate: null })
  }

  const handleTemplateChange = (_e: unknown, tpl: { idfTemplate: number; name: string } | null) => {
    updateForm({ idfTemplate: tpl?.idfTemplate ?? null })
  }

  const handlePdfTemplateChange = (
    _e: unknown,
    tpl: { idfTemplate: number; name: string } | null,
  ) => {
    updateForm({ idfPdfTemplate: tpl?.idfTemplate ?? null })
  }

  const toggleActivityType = (key: ActivityTypeKey) => {
    const current = form.activityTypes
    const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key]
    updateForm({ activityTypes: next })
  }
  const toggleAllActivityTypes = () => {
    if (form.activityTypes.length === ACTIVITY_TYPE_OPTIONS.length) {
      updateForm({ activityTypes: [] })
    } else {
      updateForm({ activityTypes: ACTIVITY_TYPE_OPTIONS.map((o) => o.value) })
    }
  }

  const toggleSeasonWeek = (id: number) => {
    const current = form.seasonWeekIds
    const next = current.includes(id) ? current.filter((i) => i !== id) : [...current, id]
    updateForm({ seasonWeekIds: next })
  }
  const toggleAllSeasonWeeks = () => {
    if (form.seasonWeekIds.length === allSeasonWeekIds.length) {
      updateForm({ seasonWeekIds: [] })
    } else {
      updateForm({ seasonWeekIds: [...allSeasonWeekIds] })
    }
  }

  const toggleMatchDay = (md: MatchDayType) => {
    const current = form.matchDayTypes
    const next = current.includes(md) ? current.filter((m) => m !== md) : [...current, md]
    updateForm({ matchDayTypes: next })
  }
  const toggleAllMatchDays = () => {
    if (form.matchDayTypes.length === MATCH_DAY_OPTIONS.length) {
      updateForm({ matchDayTypes: [] })
    } else {
      updateForm({ matchDayTypes: [...MATCH_DAY_OPTIONS] })
    }
  }

  // --- Derived ---
  const selectedTemplateObj = useMemo(() => {
    if (!form.idfTemplate || !templates) {
      return null
    }

    return templates.find((tpl) => tpl.idfTemplate === form.idfTemplate) ?? null
  }, [form.idfTemplate, templates])

  const selectedPdfTemplateObj = useMemo(() => {
    if (!form.idfPdfTemplate || !pdfTemplates) {
      return null
    }

    return pdfTemplates.find((tpl) => tpl.idfTemplate === form.idfPdfTemplate) ?? null
  }, [form.idfPdfTemplate, pdfTemplates])

  const periodLabel = useMemo(() => {
    const opt = PERIOD_OPTIONS.find((o) => o.value === form.periodType)

    return opt ? t(opt.labelKey, PERIOD_LABELS[opt.value]) : ""
  }, [form.periodType, t])

  // Section 1 — Report Type / Template (항상 상단 노출)
  const reportSection = (
    <div className="FilterPresetEditor__section">
      {nameSlot}

      {/* Report Type — Radio */}
      <div className="FilterPresetEditor__field">
        <label className="FilterPresetEditor__label">{t("reportType", "Report Type")}</label>
        <RadioGroup
          direction="row"
          value={form.reportType}
          items={[
            { value: REPORT_TYPE.DAILY, label: t("activityReport", "Activity Report") },
            { value: REPORT_TYPE.TREND, label: t("trendReport", "Trend Report") },
          ]}
          onChange={(e) => handleReportTypeChange(e.target.value as REPORT_TYPE)}
        />
      </div>

      {/* Report Template / PDF Template */}
      <div className="FilterPresetEditor__grid FilterPresetEditor__grid--2col">
        <div className="FilterPresetEditor__field">
          <label className="FilterPresetEditor__label">
            {t("reportTemplate", "Report Template")}
          </label>
          <Selector
            type="autocomplete"
            options={templates ?? []}
            value={selectedTemplateObj}
            onChange={handleTemplateChange}
            labelKey="name"
            getOptionKey={(opt: any) => String(opt.idfTemplate)}
            placeholder={t("selectTemplate", "Select template") as string}
            fullWidth
          />
        </div>
        {showPdfTemplate && (
          <div className="FilterPresetEditor__field">
            <label className="FilterPresetEditor__label">
              {t("pdfTemplate", "PDF Template")}{" "}
              <i className="FilterPresetEditor__optional">(Optional)</i>
            </label>
            <Selector
              type="autocomplete"
              options={pdfTemplates ?? []}
              value={selectedPdfTemplateObj}
              onChange={handlePdfTemplateChange}
              labelKey="name"
              getOptionKey={(opt: any) => String(opt.idfTemplate)}
              placeholder={t("selectPdfTemplate", "Select PDF template (optional)") as string}
              fullWidth
            />
          </div>
        )}
      </div>
    </div>
  )

  // Section 2 — 기간 / 액티비티타입·시즌·매치데이
  const filterSection = (
    <div className="FilterPresetEditor__section">
      {/* Row 1: Period + Date Range */}
      <div className="FilterPresetEditor__grid FilterPresetEditor__grid--3col">
        <div className="FilterPresetEditor__field">
          <label className="FilterPresetEditor__label">{t("period")}</label>
          <Selector
            type="select"
            value={form.periodType}
            onChange={(e: any) => {
              const nextType = e.target.value as FilterPresetPeriodType
              const updates: Partial<UpsertFilterPresetPayload> = { periodType: nextType }
              if (nextType === "custom" && (!form.customDateStart || !form.customDateEnd)) {
                const today = new Date()
                updates.customDateEnd = format(today, "yyyy-MM-dd")
                updates.customDateStart = format(subDays(today, 6), "yyyy-MM-dd")
              }
              updateForm(updates)
            }}
            renderValue={() => periodLabel}
            displayEmpty>
            {PERIOD_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <ListItemText primary={t(opt.labelKey, PERIOD_LABELS[opt.value])} />
              </MenuItem>
            ))}
          </Selector>
        </div>

        {/* Date Range — Period 옆에 상시 노출, custom 이 아니면 disabled */}
        <div className="FilterPresetEditor__field FilterPresetEditor__field--date-range">
          <label className="FilterPresetEditor__label">{t("dateRange", "Date Range")}</label>
          <div className="FilterPresetEditor__date-range">
            <DatePicker
              value={
                form.periodType === "custom" && form.customDateStart
                  ? parseISO(form.customDateStart)
                  : null
              }
              defaultValueNull
              onChange={(date: Date | null) =>
                updateForm({
                  customDateStart: date ? format(date, "yyyy-MM-dd") : undefined,
                })
              }
              disabled={form.periodType !== "custom"}
            />
            <span>~</span>
            <DatePicker
              value={
                form.periodType === "custom" && form.customDateEnd
                  ? parseISO(form.customDateEnd)
                  : null
              }
              defaultValueNull
              onChange={(date: Date | null) =>
                updateForm({
                  customDateEnd: date ? format(date, "yyyy-MM-dd") : undefined,
                })
              }
              disabled={form.periodType !== "custom"}
            />
          </div>
        </div>
      </div>

      {/* Row 2: Activity Type / Season / Match Day */}
      <div className="FilterPresetEditor__grid FilterPresetEditor__grid--3col">
        <div className="FilterPresetEditor__field">
          <label className="FilterPresetEditor__label">{t("activityType", "Activity Type")}</label>
          <Selector
            type="select"
            multiple
            displayEmpty
            value={form.activityTypes}
            onChange={(e: { target: { value: unknown } }) => e.target.value}
            renderValue={() =>
              form.activityTypes.length === ACTIVITY_TYPE_OPTIONS.length
                ? t("all")
                : `${form.activityTypes.length} ${t("selected", "selected")}`
            }>
            <MenuItem
              key="all-activity-type"
              onClick={toggleAllActivityTypes}
              sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}>
              <Checkbox
                checked={form.activityTypes.length === ACTIVITY_TYPE_OPTIONS.length}
                indeterminate={
                  form.activityTypes.length > 0 &&
                  form.activityTypes.length < ACTIVITY_TYPE_OPTIONS.length
                }
                size="medium"
                color={CheckboxColor.PLATINUM}
              />
              <ListItemText primary={t("all")} />
            </MenuItem>
            {ACTIVITY_TYPE_OPTIONS.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  toggleActivityType(opt.value)
                }}>
                <Checkbox checked={form.activityTypes.includes(opt.value)} size="medium" />
                <ListItemText primary={t(opt.labelKey)} />
              </MenuItem>
            ))}
          </Selector>
        </div>

        <div className="FilterPresetEditor__field">
          <label className="FilterPresetEditor__label">{t("season", "Season")}</label>
          <Selector
            type="select"
            multiple
            displayEmpty
            value={form.seasonWeekIds.map(String)}
            onChange={(e: { target: { value: unknown } }) => e.target.value}
            renderValue={() =>
              form.seasonWeekIds.length === allSeasonWeekIds.length
                ? t("all")
                : `${form.seasonWeekIds.length} ${t("selected", "selected")}`
            }>
            <MenuItem
              key="all-season-week"
              onClick={toggleAllSeasonWeeks}
              sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}>
              <Checkbox
                checked={form.seasonWeekIds.length === allSeasonWeekIds.length}
                indeterminate={
                  form.seasonWeekIds.length > 0 &&
                  form.seasonWeekIds.length < allSeasonWeekIds.length
                }
                size="medium"
                color={CheckboxColor.PLATINUM}
              />
              <ListItemText primary={t("all")} />
            </MenuItem>
            {activeSeasonWeeks.map((sw, index) => (
              <MenuItem
                key={sw.idfSeasonWeek}
                value={String(sw.idfSeasonWeek)}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  toggleSeasonWeek(sw.idfSeasonWeek as number)
                }}>
                <Checkbox
                  checked={form.seasonWeekIds.includes(sw.idfSeasonWeek as number)}
                  size="medium"
                />
                <ListItemText
                  primary={t("weekNumber", { number: index + 1 })}
                  secondary={sw.startDate && sw.endDate ? `${sw.startDate} ~ ${sw.endDate}` : ""}
                />
              </MenuItem>
            ))}
            <MenuItem
              key="none"
              value={String(NONE_SEASON_WEEK_ID)}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                toggleSeasonWeek(NONE_SEASON_WEEK_ID)
              }}>
              <Checkbox checked={form.seasonWeekIds.includes(NONE_SEASON_WEEK_ID)} size="medium" />
              <ListItemText primary={t("none", "None")} />
            </MenuItem>
          </Selector>
        </div>

        <div className="FilterPresetEditor__field">
          <label className="FilterPresetEditor__label">{t("matchDay", "Match Day")}</label>
          <Selector
            type="select"
            multiple
            displayEmpty
            value={form.matchDayTypes}
            onChange={(e: { target: { value: unknown } }) => e.target.value}
            renderValue={() =>
              form.matchDayTypes.length === MATCH_DAY_OPTIONS.length
                ? t("all")
                : `${form.matchDayTypes.length} ${t("selected", "selected")}`
            }>
            <MenuItem
              key="all-match-day"
              onClick={toggleAllMatchDays}
              sx={{ borderBottom: "1px solid rgba(0, 0, 0, 0.08)" }}>
              <Checkbox
                checked={form.matchDayTypes.length === MATCH_DAY_OPTIONS.length}
                indeterminate={
                  form.matchDayTypes.length > 0 &&
                  form.matchDayTypes.length < MATCH_DAY_OPTIONS.length
                }
                size="medium"
                color={CheckboxColor.PLATINUM}
              />
              <ListItemText primary={t("all")} />
            </MenuItem>
            {MATCH_DAY_OPTIONS.map((md) => (
              <MenuItem
                key={md}
                value={md}
                onClick={(e) => {
                  e.stopPropagation()
                  e.preventDefault()
                  toggleMatchDay(md)
                }}>
                <Checkbox checked={form.matchDayTypes.includes(md)} size="medium" />
                <ListItemText primary={md} />
              </MenuItem>
            ))}
          </Selector>
        </div>
      </div>
    </div>
  )

  // Section 3 — Players
  const playerSection = (
    <div className="FilterPresetEditor__section">
      {players && (
        <PlayerListSelector
          players={players}
          selectedPlayerIds={form.selectedPlayerIds}
          onSelectionChange={(ids) => updateForm({ selectedPlayerIds: ids })}
        />
      )}
    </div>
  )

  return (
    <div className="FilterPresetEditor__body">
      {reportSection}
      <div className="FilterPresetEditor__divider" />
      {filterSection}
      <div className="FilterPresetEditor__divider" />
      {playerSection}
    </div>
  )
}
